import React, { useMemo } from 'react';
import moment, { Moment } from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import classNames from 'classnames';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { Field } from '@atlaskit/form';
import { P100, Y200 } from '@atlaskit/theme/colors';
import Select, { ValueType } from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';
import Button, { ButtonGroup } from '@atlaskit/button';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { ENABLE_ANNUAL_TERMS, ENABLE_JCA_SWITCH_TO_ANNUAL } from '@atlassian/cssxp-utils-generic';
import {
  BillingFrequency,
  EntitlementDetails,
  FetchLinkedEntitlementsForTableQuery,
  LinkedProductTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import { entitlementUtils, useFeatureToggle } from '@css-xp/base';
import { isStatuspage, isJPDProduct, isAtlasProduct } from '@atlassian/cssxp-utils-product';
import {
  isFreePlanEntitlement,
  getParentContainerId,
  getCorrectedCurrentDateForAnnual,
} from '@atlassian/cssxp-utils-entitlement';
import { PurchaseFor } from '@atlassian/cssxp-typescommon';
import { FORM_NAMES } from './constants';
import { FormState } from './manage-entitlement-v2';

export interface TermLengthSelectItem {
  label: string;
  value: number;
}

interface GetIsSwitchingDisabledProps {
  entitlement: EntitlementDetails;
  isStatusPageProduct: boolean;
  hasAnnualLinkedEntitlements: boolean;
  isAnnualTermEnabled: boolean;
  isAnnualJCAEnabled: boolean;
  isDisabled: boolean;
  endTimestamp: number | null | undefined;
}

export interface ManageBillingCycleProps {
  entitlement: EntitlementDetails;
  formState: FormState;
  linkedEntitlementsData: FetchLinkedEntitlementsForTableQuery | undefined;
  linkedEntitlementsLoading: boolean;
  linkedEntitlementsError: Error | undefined;
  hasAnnualLinkedEntitlements: boolean;
  updateFormState: (updatedState: FormState) => void;
  isBillingCycleChanged: boolean;
  isMonthlySelected: boolean;
  isAnnualSelected: boolean;
  isAnnualTermedSelected: boolean;
  isDisabled: boolean;
  isAnnualAutoRenewSelected: boolean;
  currentDate: Moment;
}

export const TERM_LENGTH_OPTIONS: TermLengthSelectItem[] = [
  {
    label: '12 months',
    value: 12,
  },
];

const BILLING_FREQUENCY_OPTIONS = {
  MONTHLY: 'Monthly',
  ANNUAL: 'Annual - Auto renew',
  ANNUAL_TERMED: 'Annual - Termed',
};

const getIsSwitchingDisabled = ({
  entitlement,
  isStatusPageProduct,
  hasAnnualLinkedEntitlements,
  isAnnualTermEnabled,
  isAnnualJCAEnabled,
  endTimestamp,
  isDisabled,
}: GetIsSwitchingDisabledProps) => {
  if (isDisabled) {
    return {
      [BILLING_FREQUENCY_OPTIONS.MONTHLY]: true,
      [BILLING_FREQUENCY_OPTIONS.ANNUAL]: true,
      [BILLING_FREQUENCY_OPTIONS.ANNUAL_TERMED]: true,
      [PurchaseFor.ANNUAL_AUTO_RENEW]: true,
      [PurchaseFor.ANNUAL_TERM]: true,
    };
  }
  if (isStatusPageProduct) {
    return {
      [BILLING_FREQUENCY_OPTIONS.MONTHLY]: false,
      [BILLING_FREQUENCY_OPTIONS.ANNUAL]:
        entitlement.billingFrequency === BillingFrequency.ANNUAL
          ? false
          : !(
              !entitlement.edition?.toLowerCase().includes('trial') ||
              !entitlement.edition?.toLowerCase().includes('free')
            ),
      [BILLING_FREQUENCY_OPTIONS.ANNUAL_TERMED]: true,
    };
  }

  const isEnabledProduct =
    entitlement?.productType === LinkedProductTypes.CONFLUENCE ||
    entitlement?.productType === LinkedProductTypes.JIRA ||
    isJPDProduct(entitlement?.productId) ||
    isAtlasProduct(entitlement?.productId);

  if (entitlement.billingFrequency === BillingFrequency.ANNUAL) {
    if (endTimestamp) {
      return {
        [BILLING_FREQUENCY_OPTIONS.MONTHLY]: true,
        [BILLING_FREQUENCY_OPTIONS.ANNUAL]: false,
        [PurchaseFor.ANNUAL_AUTO_RENEW]: true,
        [PurchaseFor.ANNUAL_TERM]: false,
      };
    }

    return {
      [BILLING_FREQUENCY_OPTIONS.MONTHLY]: true,
      [BILLING_FREQUENCY_OPTIONS.ANNUAL]: false,
      [BILLING_FREQUENCY_OPTIONS.ANNUAL_TERMED]: !isEnabledProduct || !isAnnualTermEnabled,
      [PurchaseFor.ANNUAL_AUTO_RENEW]: false,
      [PurchaseFor.ANNUAL_TERM]: true,
    };
  }

  return {
    [BILLING_FREQUENCY_OPTIONS.MONTHLY]: isFreePlanEntitlement(entitlement) ? hasAnnualLinkedEntitlements : false,
    [BILLING_FREQUENCY_OPTIONS.ANNUAL]: !isEnabledProduct || !isAnnualJCAEnabled,
    [BILLING_FREQUENCY_OPTIONS.ANNUAL_TERMED]: !isEnabledProduct || !isAnnualTermEnabled,
    [PurchaseFor.ANNUAL_AUTO_RENEW]:
      isFreePlanEntitlement(entitlement) && hasAnnualLinkedEntitlements && !!endTimestamp,
    [PurchaseFor.ANNUAL_TERM]: isFreePlanEntitlement(entitlement) && hasAnnualLinkedEntitlements && !endTimestamp,
  };
};

const BillingCycleLabel = () => (
  <div>
    Billing cycle
    <Tooltip content="Free plans aren't eligible for annual billing" tag={'span'}>
      <InfoIcon size="small" testId="info-icon" label="Info" />
    </Tooltip>
  </div>
);

export const ManageBillingCycle = ({
  entitlement,
  formState,
  linkedEntitlementsData,
  linkedEntitlementsLoading,
  linkedEntitlementsError,
  hasAnnualLinkedEntitlements,
  updateFormState,
  isBillingCycleChanged,
  isMonthlySelected,
  isAnnualSelected,
  isAnnualTermedSelected,
  isDisabled,
  isAnnualAutoRenewSelected,
  currentDate,
}: ManageBillingCycleProps) => {
  const [isEnabled] = useFeatureToggle();
  const isAnnualJCAEnabled = isEnabled(ENABLE_JCA_SWITCH_TO_ANNUAL);
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  const isStatusPageProduct = isStatuspage(entitlement.productId);
  const endTimestamp = formState?.endTimestamp;
  const isSwitchingDisabled = getIsSwitchingDisabled({
    entitlement,
    hasAnnualLinkedEntitlements,
    isStatusPageProduct,
    isAnnualTermEnabled,
    isAnnualJCAEnabled,
    endTimestamp,
    isDisabled,
  });
  const termLengthValue = formState?.termLength ?? TERM_LENGTH_OPTIONS[0];
  const linkedEntitlementDetails = useMemo(() => {
    if (
      !isBillingCycleChanged &&
      formState.billingFrequency === BillingFrequency.ANNUAL &&
      linkedEntitlementsData?.entitlementDetails?.linkedEntitlements &&
      linkedEntitlementsData?.entitlementDetails?.linkedEntitlements?.length > 0
    ) {
      const data = cloneDeep(linkedEntitlementsData);
      data.entitlementDetails.linkedEntitlements = entitlement.isApp
        ? []
        : data?.entitlementDetails?.linkedEntitlements?.filter((item) => !!item?.isApp);

      return data;
    }

    return linkedEntitlementsData;
  }, [linkedEntitlementsData, formState.billingFrequency, isBillingCycleChanged]);

  const renderLinkedEntitlementsTable = () => {
    return (
      (isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL) &&
      linkedEntitlementDetails?.entitlementDetails?.linkedEntitlements &&
      linkedEntitlementDetails.entitlementDetails.linkedEntitlements.length > 0 && (
        <div className="linked-entitlements">
          {isBillingCycleChanged && (
            <div className="warning-message">
              <InfoIcon size="small" testId="info-icon" label="Info" primaryColor={P100} />
              <b>Keep in mind</b>
              <span>billing cycle changes will apply to other subscriptions linked to this product family.</span>
            </div>
          )}
          <entitlementUtils.LinkedEntitlementsTable
            message=" "
            header="Linked subscriptions that this change will apply to"
            parentContainerID={getParentContainerId(entitlement)}
            productType={entitlement.productType}
            linkedEntitlementsData={linkedEntitlementDetails}
            linkedEntitlementsLoading={linkedEntitlementsLoading}
            linkedEntitlementsError={linkedEntitlementsError}
          />
        </div>
      )
    );
  };

  if (isAnnualTermEnabled) {
    return (
      <div className="billing-cycle-view">
        <h2>Select billing cycle</h2>
        <Field
          name={FORM_NAMES.BILLING_FREQUENCY}
          label={<BillingCycleLabel />}
          defaultValue={formState.billingFrequency}
        >
          {({ fieldProps }) => {
            return (
              <div className="buttonGroup-container">
                <ButtonGroup>
                  <Button
                    isDisabled={isSwitchingDisabled[BILLING_FREQUENCY_OPTIONS.MONTHLY]}
                    id="billFrequency-monthly"
                    testId="billFrequency-monthly"
                    appearance={isMonthlySelected ? 'primary' : 'default'}
                    onClick={() => {
                      fieldProps.onChange(BillingFrequency.MONTHLY);
                      updateFormState({
                        billingFrequency: BillingFrequency.MONTHLY,
                        termLength: null,
                        endTimestamp: null,
                        purchaseFor: null,
                      });
                    }}
                    className={classNames({ ['selected']: isMonthlySelected })}
                    selected={isMonthlySelected}
                  >
                    {BILLING_FREQUENCY_OPTIONS.MONTHLY}
                  </Button>
                  <Button
                    isDisabled={isSwitchingDisabled[BILLING_FREQUENCY_OPTIONS.ANNUAL]}
                    id="billFrequency-annual"
                    testId="billFrequency-annual"
                    appearance={isAnnualSelected ? 'primary' : 'default'}
                    onClick={() => {
                      fieldProps.onChange(BillingFrequency.ANNUAL);
                      updateFormState({
                        billingFrequency: BillingFrequency.ANNUAL,
                      });
                    }}
                    className={classNames({ ['selected']: isAnnualSelected })}
                    selected={isAnnualSelected}
                  >
                    Annual
                  </Button>
                </ButtonGroup>
              </div>
            );
          }}
        </Field>
        {formState.billingFrequency === BillingFrequency.ANNUAL && !isStatusPageProduct && (
          <div>
            <Field name={FORM_NAMES.PURCHASE_FOR} label={'Purchase for'} defaultValue={formState.purchaseFor}>
              {({ fieldProps }) => (
                <div className="buttonGroup-container-purchase-for">
                  <ButtonGroup>
                    <Button
                      isDisabled={isSwitchingDisabled[PurchaseFor.ANNUAL_AUTO_RENEW]}
                      id="annual-auto-renew"
                      testId="annual-auto-renew"
                      appearance={isAnnualAutoRenewSelected ? 'primary' : 'default'}
                      onClick={() => {
                        fieldProps.onChange(PurchaseFor.ANNUAL_AUTO_RENEW);
                        updateFormState({
                          ...formState,
                          purchaseFor: PurchaseFor.ANNUAL_AUTO_RENEW,
                          termLength: null,
                          endTimestamp: null,
                          coBillOption: false,
                          coBillTimestamp: null,
                        });
                      }}
                      className={classNames({ ['selected']: isAnnualAutoRenewSelected })}
                      selected={isAnnualAutoRenewSelected}
                    >
                      Auto-renew
                    </Button>
                    <Button
                      isDisabled={isSwitchingDisabled[PurchaseFor.ANNUAL_TERM]}
                      id="annual-termed"
                      testId="annual-termed"
                      appearance={isAnnualTermedSelected ? 'primary' : 'default'}
                      onClick={() => {
                        fieldProps.onChange(PurchaseFor.ANNUAL_TERM);
                        let updatedState = {
                          ...formState,
                          purchaseFor: PurchaseFor.ANNUAL_TERM,
                        };
                        if (isBillingCycleChanged) {
                          updatedState = {
                            ...updatedState,
                            endTimestamp: moment
                              .utc(getCorrectedCurrentDateForAnnual(currentDate))
                              .add(termLengthValue?.value, 'months')
                              .valueOf(),
                            termLength: termLengthValue,
                            coBillOption: false,
                            coBillTimestamp: null,
                          };
                        }
                        updateFormState(updatedState);
                      }}
                      className={classNames({ ['selected']: isAnnualTermedSelected })}
                      selected={isAnnualTermedSelected}
                    >
                      Termed
                    </Button>
                  </ButtonGroup>
                </div>
              )}
            </Field>
            {isBillingCycleChanged && !isStatusPageProduct && formState?.billingFrequency === BillingFrequency.ANNUAL && (
              <Field<ValueType<TermLengthSelectItem>, HTMLSelectElement>
                label="Billing period"
                name={FORM_NAMES.TERM_LENGTH}
                id="termLength"
                defaultValue={termLengthValue}
              >
                {({ fieldProps }) => (
                  <div className="term-length-select-container" data-testid="termLength">
                    <Select<TermLengthSelectItem>
                      {...fieldProps}
                      isSearchable={false}
                      options={isAnnualTermedSelected ? TERM_LENGTH_OPTIONS : [TERM_LENGTH_OPTIONS[0]]}
                      isDisabled={isDisabled || isAnnualAutoRenewSelected}
                      testId="term-length-select"
                      placeholder="Select term length..."
                      onChange={(termLengthItem: ValueType<TermLengthSelectItem, false>) => {
                        fieldProps.onChange(termLengthItem);
                        updateFormState({
                          ...formState,
                          termLength: termLengthItem,
                          endTimestamp: moment
                            .utc(getCorrectedCurrentDateForAnnual(currentDate))
                            .add(termLengthItem?.value as number, 'months')
                            .valueOf(),
                        });
                      }}
                    />
                  </div>
                )}
              </Field>
            )}
          </div>
        )}
        {isFreePlanEntitlement(entitlement) && isAnnualSelected && (
          <div className="free-to-annual-warning" data-testid="free-to-annual-warning">
            <WarningIcon label="" primaryColor={Y200} />
            <p>
              Free plans aren’t eligible for annual billing.
              <span> Select ‘Continue’ to choose another plan.</span>
            </p>
          </div>
        )}
        {renderLinkedEntitlementsTable()}
      </div>
    );
  }

  return (
    <div className="billing-cycle-view">
      <h2>Select billing cycle</h2>
      <Field
        name={FORM_NAMES.BILLING_FREQUENCY}
        label={<BillingCycleLabel />}
        defaultValue={formState.billingFrequency}
      >
        {({ fieldProps }) => {
          return (
            <div className="buttonGroup-container">
              <ButtonGroup>
                <Button
                  isDisabled={isSwitchingDisabled[BILLING_FREQUENCY_OPTIONS.MONTHLY]}
                  id="billFrequency-monthly"
                  testId="billFrequency-monthly"
                  appearance={isMonthlySelected ? 'primary' : 'default'}
                  onClick={() => {
                    fieldProps.onChange(BillingFrequency.MONTHLY);
                    updateFormState({
                      billingFrequency: BillingFrequency.MONTHLY,
                      termLength: null,
                    });
                  }}
                  className={classNames({ ['selected']: isMonthlySelected })}
                  selected={isMonthlySelected}
                >
                  {BILLING_FREQUENCY_OPTIONS.MONTHLY}
                </Button>
                <Button
                  isDisabled={isSwitchingDisabled[BILLING_FREQUENCY_OPTIONS.ANNUAL]}
                  id="billFrequency-annual"
                  testId="billFrequency-annual"
                  appearance={isAnnualSelected ? 'primary' : 'default'}
                  onClick={() => {
                    fieldProps.onChange(BillingFrequency.ANNUAL);
                    updateFormState({
                      billingFrequency: BillingFrequency.ANNUAL,
                    });
                  }}
                  className={classNames({ ['selected']: isAnnualSelected })}
                  selected={isAnnualSelected}
                >
                  {BILLING_FREQUENCY_OPTIONS.ANNUAL}
                </Button>
                {!isStatusPageProduct && (
                  <Button
                    isDisabled={isSwitchingDisabled[BILLING_FREQUENCY_OPTIONS.ANNUAL_TERMED]}
                    id="billFrequency-annual-termed"
                    testId="billFrequency-annual-termed"
                    appearance={isAnnualTermedSelected ? 'primary' : 'default'}
                    className={classNames({ ['selected']: isAnnualTermedSelected })}
                    selected={isAnnualTermedSelected}
                  >
                    {BILLING_FREQUENCY_OPTIONS.ANNUAL_TERMED}
                  </Button>
                )}
              </ButtonGroup>
            </div>
          );
        }}
      </Field>
      {!isStatusPageProduct && isAnnualTermedSelected && (
        <Field<ValueType<TermLengthSelectItem>, HTMLSelectElement>
          label="Purchase for"
          name={FORM_NAMES.TERM_LENGTH}
          id="termLength"
          defaultValue={formState.termLength}
        >
          {({ fieldProps }) => (
            <div className="term-length-select-container" data-testid="termLength">
              <Select<TermLengthSelectItem>
                {...fieldProps}
                isSearchable={false}
                options={TERM_LENGTH_OPTIONS}
                placeholder="Select term length..."
              />
            </div>
          )}
        </Field>
      )}
      {renderLinkedEntitlementsTable()}
    </div>
  );
};
